import useLocale from 'next-translate/useTranslation';

import clsxm from '@/lib/clsxm';

import {
  TESTIMONIALS,
  type TestimonialSource,
  type TextTestimonial,
} from '@/constants/testimonials';

import Halftone from './Halftone';
import { SectionHeading, tone } from './primitives';

const SOURCE_NAMES: Record<TestimonialSource, string> = {
  capterra: 'Capterra',
  g2: 'G2',
  google: 'Google',
  trustpilot: 'Trustpilot',
  youtube: 'YouTube',
};

// Verbatim excerpts of real reviews (ellipses mark the cuts). Quotes stay in
// the reviewer's own words, so they are not translated.
const PICKS: { id: string; excerpt: string }[] = [
  {
    id: 'nermin-s',
    excerpt:
      'Easy-Peasy.AI is an incredible chatbot platform! It’s user-friendly, intelligent, and provides accurate and helpful responses. I’m amazed by its capabilities and how it adapts to my needs.',
  },
  {
    id: 'ronit-s',
    excerpt:
      'The intuitive interface without needing any technical expertise. Use runs fast and the results are highly accurate. Plus, the affordable pricing plans make powerful AI accessible even for individuals and small businesses.',
  },
  {
    id: 'dennis-cambronero',
    excerpt:
      '…I use it to create images, bots and access every LLM in one single place. I highly recommend it.',
  },
];

export default function AgentReviews() {
  const { t } = useLocale('chatbots');

  const reviews = PICKS.flatMap(({ id, excerpt }) => {
    const review = TESTIMONIALS.find(
      (item): item is TextTestimonial => item.id === id && item.type === 'text',
    );
    return review ? [{ ...review, excerpt }] : [];
  });

  if (reviews.length === 0) return null;
  const [featured, ...rest] = reviews;

  return (
    <section id='reviews' className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('What customers say.')}
          tail={t('Straight from G2, Capterra and Trustpilot.')}
        />

        <div className='mt-14 grid gap-5 lg:grid-cols-5'>
          <ReviewCard review={featured} featured className='lg:col-span-3' />
          <div className='grid gap-5 lg:col-span-2'>
            {rest.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ReviewCard({
  review,
  featured = false,
  className,
}: {
  review: TextTestimonial & { excerpt: string };
  featured?: boolean;
  className?: string;
}) {
  const { t } = useLocale('chatbots');
  const source = review.source ? SOURCE_NAMES[review.source] : null;

  return (
    <figure
      className={clsxm(
        'relative isolate flex flex-col justify-between gap-10 overflow-hidden rounded-[28px] p-7 sm:p-9',
        featured
          ? 'bg-[#0d0c1f] ring-1 ring-inset ring-white/10'
          : clsxm(tone.surface, tone.line),
        className,
      )}
    >
      {featured && (
        <Halftone
          at='100% 100%'
          reach={75}
          className='-z-10 opacity-40 [--ht:rgb(var(--tw-color-primary-700))]'
        />
      )}
      <blockquote lang='en' dir='ltr' className='text-start'>
        {featured && (
          <span
            aria-hidden='true'
            className='block h-14 text-[5.5rem] font-semibold leading-none text-primary-400'
          >
            “
          </span>
        )}
        <p
          className={clsxm(
            featured
              ? 'text-2xl font-medium leading-[1.4] tracking-[-0.01em] text-white sm:text-[2rem] sm:leading-[1.35]'
              : clsxm('text-[17px] leading-8', tone.ink),
          )}
        >
          {featured ? review.excerpt : `“${review.excerpt}”`}
        </p>
      </blockquote>
      <figcaption className='flex items-center gap-3.5'>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={review.avatarUrl}
          alt=''
          width={40}
          height={40}
          loading='lazy'
          className='h-10 w-10 flex-none rounded-full bg-[#eeeef4] object-cover'
        />
        <span className='min-w-0 flex-1'>
          <span
            className={clsxm(
              'block text-[15px] font-semibold',
              featured ? 'text-white' : tone.ink,
            )}
          >
            {review.author}
          </span>
          <span className='flex items-center gap-2 text-sm'>
            <span className='text-[#f5a524]' aria-label={t('5 out of 5 stars')}>
              ★★★★★
            </span>
            {source && review.sourceUrl && (
              <a
                href={review.sourceUrl}
                target='_blank'
                rel='noopener noreferrer'
                className={clsxm(
                  'truncate hover:underline',
                  featured ? 'text-[#9d9dbd]' : tone.quiet,
                )}
              >
                {source}
              </a>
            )}
          </span>
        </span>
      </figcaption>
    </figure>
  );
}
